import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Button, CircularProgress, IconButton, Tooltip} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {Fullscreen, FullscreenExit} from '@mui/icons-material';
import {getRestrictedZones} from '../../api/branch/restrictedZoneApiClient';

// Same palette and order as RestrictedZoneDialog, so an area keeps the colour
// it was drawn in when you see it again over the live stream.
const ZONE_COLORS = ['#e53935', '#1e88e5', '#43a047', '#fb8c00', '#8e24aa', '#00897b'];
const zoneColorFor = (index) => ZONE_COLORS[index % ZONE_COLORS.length];

/**
 * Simple MJPEG stream player using public URLs with tokens
 * @param {number} cameraId - The camera ID for the stream
 * @param {string} streamUrl - The public stream URL with token
 * @param {Object} style - Custom styles for the video player
 * @param {boolean} showLoading - Whether to show loading spinner
 * @param {Function} onError - Error callback function
 * @param {Function} onLoad - Load callback function
 */
const VideoPlayer = ({
                         cameraId,
                         streamUrl,
                         style = {},
                         showLoading = true,
                         showZones = true,
                         onError,
                         onLoad,
                         ...props
                     }) => {
    const theme = useTheme();
    const imgRef = useRef(null);
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const videoStyle = {
        width: '100%',
        height: 'auto',
        maxWidth: '100%',
        ...style
    };

    // Add CSS for fullscreen video and streaming optimizations
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .video-player-container:fullscreen {
                background: black;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            .video-player-container:fullscreen img {
                max-width: 100vw;
                max-height: 100vh;
                width: auto;
                height: auto;
            }
            .video-player-container:-webkit-full-screen {
                background: black;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            .video-player-container:-webkit-full-screen img {
                max-width: 100vw;
                max-height: 100vh;
                width: auto;
                height: auto;
            }
            .video-player-container:-moz-full-screen {
                background: black;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            .video-player-container:-moz-full-screen img {
                max-width: 100vw;
                max-height: 100vh;
                width: auto;
                height: auto;
            }
            /* Optimize streaming performance */
            .video-player-container img {
                will-change: transform;
                backface-visibility: hidden;
                transform: translateZ(0);
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        if (!streamUrl) return;

        setLoading(true);
        setError(null);
        setIsLoaded(false);
        console.log('VideoPlayer: Using public stream URL:', streamUrl);
        console.log('VideoPlayer: Camera ID:', cameraId);

        // Remove aggressive cache busting that causes frame jumps
        // MJPEG streams handle their own frame updates naturally

        // Auto-clear loading state after 1.5 seconds for faster response
        const loadingTimeout = setTimeout(() => {
            console.log('VideoPlayer: Auto-clearing loading state for camera:', cameraId);
            setLoading(false);
        }, 1500);

        return () => {
            clearTimeout(loadingTimeout);
            // Clean up any existing image source to prevent memory leaks
            if (imgRef.current) {
                imgRef.current.src = '';
            }
        };
    }, [streamUrl, cameraId]);

    // Restricted areas for this camera, drawn over the stream. Purely
    // informational here — the editor lives in the camera configuration — so a
    // failure just means no outlines, never a broken stream.
    useEffect(() => {
        if (!showZones || !cameraId) {
            setZones([]);
            return;
        }
        let cancelled = false;
        getRestrictedZones(cameraId)
            .then((data) => {
                if (!cancelled) setZones(data || []);
            })
            .catch(() => {
                if (!cancelled) setZones([]);
            });
        return () => {
            cancelled = true;
        };
    }, [cameraId, showZones]);

    // Retry function
    const handleRetry = () => {
        setError(null);
        setLoading(true);
        setIsLoaded(false);
        // Force reload the image
        if (imgRef.current) {
            imgRef.current.src = streamUrl + '?t=' + Date.now();
        }
    };

    // Fullscreen functionality - make the entire video player container fullscreen
    const toggleFullscreen = async () => {
        const videoContainer = imgRef.current?.closest('.video-player-container');
        if (!videoContainer) return;

        try {
            if (!isFullscreen) {
                // Enter fullscreen
                if (videoContainer.requestFullscreen) {
                    await videoContainer.requestFullscreen();
                } else if (videoContainer.webkitRequestFullscreen) {
                    await videoContainer.webkitRequestFullscreen();
                } else if (videoContainer.msRequestFullscreen) {
                    await videoContainer.msRequestFullscreen();
                }
                setIsFullscreen(true);
            } else {
                // Exit fullscreen
                if (document.exitFullscreen) {
                    await document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    await document.webkitExitFullscreen();
                } else if (document.msExitFullscreen) {
                    await document.msExitFullscreen();
                }
                setIsFullscreen(false);
            }
        } catch (err) {
            console.error('Fullscreen error:', err);
        }
    };

    // Listen for fullscreen changes and keyboard events
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement
            );
            setIsFullscreen(isCurrentlyFullscreen);
        };

        const handleKeyDown = (event) => {
            // ESC key to exit fullscreen
            if (event.key === 'Escape' && isFullscreen) {
                toggleFullscreen();
            }
            // F key to toggle fullscreen
            if (event.key === 'f' || event.key === 'F') {
                event.preventDefault();
                toggleFullscreen();
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isFullscreen]);

    // Handle error
    if (error) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                style={videoStyle}
                sx={{
                    backgroundColor: theme.palette.grey[100],
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    color: '#666',
                    gap: 2,
                    p: 2,
                    minHeight: '300px'
                }}
            >
                <div>Stream Error: {error.message || 'Failed to load stream'}</div>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleRetry}
                >
                    Retry Stream
                </Button>
            </Box>
        );
    }

    // Show loading spinner only briefly
    if (loading && showLoading && !isLoaded) {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={videoStyle}
                sx={{
                    backgroundColor: theme.palette.grey[100],
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    minHeight: '300px'
                }}
            >
                <CircularProgress size={40}/>
            </Box>
        );
    }

    // Show video player
    console.log('VideoPlayer: Rendering video player with URL:', streamUrl);
    return (
        <Box style={videoStyle} className="video-player-container">
            {streamUrl && (
                <div style={{position: 'relative', width: '100%', height: '100%'}}>
                    {/* Show loading overlay if still loading */}
                    {loading && showLoading && (
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                backgroundColor: 'rgba(245, 245, 245, 0.8)',
                                zIndex: 1
                            }}
                        >
                            <CircularProgress size={40}/>
                        </Box>
                    )}

                    {/* Fullscreen button */}
                    {isLoaded && (
                        <Box
                            position="absolute"
                            top={10}
                            right={10}
                            zIndex={2}
                            sx={{
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                borderRadius: '4px'
                            }}
                        >
                            <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
                                <IconButton
                                    onClick={toggleFullscreen}
                                    sx={{color: 'white'}}
                                    size="small"
                                >
                                    {isFullscreen ? <FullscreenExit/> : <Fullscreen/>}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    {/* Optimized img element for fast MJPEG streaming */}
                <div style={{position: 'relative', width: '100%', lineHeight: 0}}>
                <img
                    ref={imgRef}
                    src={streamUrl}
                    alt="Live Stream"
                    style={{
                        width: '100%',
                        height: 'auto',
                        minHeight: '300px',
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        display: 'block',
                        // Optimize for fast streaming
                        imageRendering: 'auto',
                        imageOrientation: 'none'
                    }}
                    // Optimized attributes for faster streaming
                    crossOrigin="anonymous"
                    loading="eager"
                    decoding="sync"
                    // Disable browser optimizations that might slow down streaming
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                    onLoad={() => {
                        console.log('VideoPlayer: Stream loaded successfully for camera:', cameraId);
                        console.log('VideoPlayer: Image dimensions:', imgRef.current?.naturalWidth, 'x', imgRef.current?.naturalHeight);
                        setLoading(false);
                        setIsLoaded(true);
                        if (onLoad) onLoad();
                    }}
                    onError={(e) => {
                        console.error('VideoPlayer: Stream load error for camera:', cameraId, e);
                        console.error('VideoPlayer: Error details:', e.target?.error || e.message);
                        setError(new Error('Failed to load stream'));
                        setLoading(false);
                        setIsLoaded(false);
                        if (onError) onError(e);
                    }}
                    {...props}
                />

                {/* Restricted-area outlines. Points are normalised 0..1, the
                    same units the editor saves, so the viewBox needs no scaling
                    maths and the overlay survives any resolution change.
                    pointer-events:none keeps fullscreen and controls clickable. */}
                {isLoaded && zones.length > 0 && (
                    <svg
                        viewBox="0 0 1 1"
                        preserveAspectRatio="none"
                        style={{
                            position: 'absolute', inset: 0,
                            width: '100%', height: '100%',
                            pointerEvents: 'none', zIndex: 1,
                        }}
                    >
                        {zones.map((zone, index) => {
                            const polygon = zone.polygon || [];
                            if (polygon.length < 3) return null;
                            const color = zoneColorFor(index);
                            const points = polygon.map((p) => `${p.x},${p.y}`).join(' ');
                            return (
                                <g key={zone.zoneId ?? index} opacity={zone.enabled ? 1 : 0.45}>
                                    <polygon
                                        points={points}
                                        fill={color}
                                        fillOpacity={zone.enabled ? 0.18 : 0.06}
                                        stroke={color}
                                        strokeWidth={2}
                                        strokeDasharray={zone.enabled ? undefined : '6 4'}
                                        strokeLinejoin="round"
                                        vectorEffect="non-scaling-stroke"
                                    />
                                </g>
                            );
                        })}
                    </svg>
                )}

                {/* Zone names, as HTML so they render at a real px size
                    regardless of frame resolution. */}
                {isLoaded && zones.length > 0 && (
                    <div style={{position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2}}>
                        {zones.map((zone, index) => {
                            const polygon = zone.polygon || [];
                            if (polygon.length < 3 || !zone.name) return null;
                            const top = polygon.reduce((best, pt) => (pt.y < best.y ? pt : best), polygon[0]);
                            return (
                                <span
                                    key={zone.zoneId ?? index}
                                    style={{
                                        position: 'absolute',
                                        left: `${top.x * 100}%`,
                                        top: `${top.y * 100}%`,
                                        transform: 'translate(-50%, -115%)',
                                        whiteSpace: 'nowrap',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        lineHeight: 1.4,
                                        padding: '1px 6px',
                                        borderRadius: 4,
                                        color: '#fff',
                                        backgroundColor: zoneColorFor(index),
                                        opacity: zone.enabled ? 0.95 : 0.5,
                                    }}
                                >
                                    {zone.name}
                                </span>
                            );
                        })}
                    </div>
                )}
                </div>
                </div>
            )}
        </Box>
    );
};

VideoPlayer.propTypes = {
    cameraId: PropTypes.number.isRequired,
    streamUrl: PropTypes.string,
    style: PropTypes.object,
    showLoading: PropTypes.bool,
    showZones: PropTypes.bool,
    onError: PropTypes.func,
    onLoad: PropTypes.func
};

export default VideoPlayer;